import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  User,
  ShieldCheck,
  Store,
  Building2,
  Clock,
  Zap,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Lock,
  PackageOpen,
  ShoppingBag,
  Car,
  Wrench,
  MessageSquare,
  Truck,
  ExternalLink,
  ChevronRight,
  ShieldAlert,
  Headphones,
  Check,
} from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { appRoutes } from '@/constants'
import { useAuth } from '@/providers/auth-provider'
import {
  useCustomerDashboard,
  useCustomerApprovalStatus,
  useCustomerSubscription,
  useCustomerPermissions,
  useCustomerSubmissionHistory,
} from '../../hooks/use-customer'
import { useMyOrders } from '@/modules/cart/hooks/use-cart'

export function CustomerDashboardPage() {
  const navigate = useNavigate()
  const { user, role } = useAuth()
  const { data: dashboard, isLoading: isDashboardLoading } = useCustomerDashboard()
  const { data: approval } = useCustomerApprovalStatus()
  const { data: subscription } = useCustomerSubscription()
  const { data: permissions } = useCustomerPermissions()
  const { data: history } = useCustomerSubmissionHistory()
  const { data: orders = [], isLoading: isOrdersLoading } = useMyOrders()

  const pendingRequest = history?.find((h) => h.status === 'PENDING')
  const isApprovedMerchant = role === 'SELLER' || role === 'DEALER'

  // Time of day greeting
  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  const permissionList = [
    { label: 'Create car listings', allowed: permissions?.canCreateCar?.allowed },
    { label: 'Create parts listings', allowed: permissions?.canCreatePart?.allowed },
    { label: 'Boost vehicle visibility', allowed: permissions?.canBoostListing?.allowed },
    { label: 'Purchase dealer leads', allowed: permissions?.canPurchaseLead?.allowed },
    { label: 'Business analytics suite', allowed: permissions?.canAccessAnalytics?.allowed },
  ]

  const formatCurrency = (val: any) => {
    if (val === null || val === undefined) return '₹0'
    const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9.]/g, ''))
    if (isNaN(num)) return '₹0'
    return `₹${num.toLocaleString('en-IN')}`
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* ── 1. LUXURY EXECUTIVE WELCOME HERO BANNER ────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-gradient-to-br from-card via-card/95 to-slate-900/60 p-6 sm:p-8 md:p-10 shadow-xl">
        {/* Background Ambient Glows */}
        <div
          aria-hidden="true"
          className="absolute -right-20 -top-20 size-72 rounded-full bg-[#CF2F2F]/15 blur-3xl pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-20 left-1/3 size-72 rounded-full bg-purple-500/10 blur-3xl pointer-events-none"
        />

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-3 max-w-2xl">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-[#CF2F2F]/15 text-[#CF2F2F] border border-[#CF2F2F]/30 font-bold uppercase text-[10px] tracking-wider px-2.5 py-0.5">
                Client Control Hub
              </Badge>
              <Badge variant="outline" className="text-[10px] font-semibold text-muted-foreground border-border/70">
                Member ID: #{user?.id || '1001'}
              </Badge>
              {dashboard?.isEmailVerified && (
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[10px] font-semibold flex items-center gap-1">
                  <Check className="size-3" /> Email Verified
                </Badge>
              )}
            </div>

            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold text-foreground tracking-tight leading-tight">
              {greeting}, <span className="text-[#CF2F2F]">{user?.firstName || 'Valued Client'}</span>
            </h1>

            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Welcome to your personal automotive command center. Oversee your spare parts orders, communicate with
              dealers, explore verified supercars, or upgrade your account to start selling.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Button asChild className="bg-[#CF2F2F] hover:bg-[#b52626] text-white font-bold gap-2 shadow-md">
                <Link to={appRoutes.cars}>
                  <Car className="size-4" /> Explore Cars
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border hover:bg-accent font-bold gap-2">
                <Link to={appRoutes.parts}>
                  <Wrench className="size-4" /> Parts Catalog
                </Link>
              </Button>
              <Button asChild variant="ghost" className="font-bold gap-2 text-foreground/80 hover:text-foreground">
                <Link to={appRoutes.customer.orders}>
                  <PackageOpen className="size-4" /> My Orders ({orders.length})
                </Link>
              </Button>
            </div>
          </div>

          {/* Quick Client Profile Card */}
          <div className="shrink-0 flex flex-col justify-center rounded-xl border border-border/70 bg-background/60 p-5 backdrop-blur-md min-w-[240px] space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex size-11 items-center justify-center rounded-xl bg-[#CF2F2F]/10 text-[#CF2F2F] font-display text-base font-extrabold border border-[#CF2F2F]/20">
                {user?.firstName?.[0] || 'U'}
                {user?.lastName?.[0] || ''}
              </div>
              <div className="min-w-0">
                <p className="truncate font-bold text-sm text-foreground">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ''}` : 'Customer'}
                </p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            <div className="pt-2 border-t border-border/60 space-y-1.5 text-xs">
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Account Role</span>
                <span className="font-bold text-foreground">{dashboard?.role || role || 'CUSTOMER'}</span>
              </div>
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Merchant Status</span>
                <span className="font-bold text-[#CF2F2F]">
                  {isApprovedMerchant ? 'Approved Merchant' : pendingRequest ? 'Review Pending' : 'Individual Buyer'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 2. METRICS & KPI OVERVIEW (4 BALANCED CARDS) ────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Metric 1: Parts Orders */}
        <Card
          className="border-border/80 bg-card/80 backdrop-blur-xl hover:border-blue-500/50 transition-all cursor-pointer group"
          onClick={() => navigate(appRoutes.customer.orders)}
        >
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500 group-hover:scale-105 transition-transform">
                <PackageOpen className="size-5.5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Spare Parts Orders</p>
                <p className="font-display text-2xl font-bold text-foreground mt-0.5">
                  {isOrdersLoading ? '...' : orders.length}
                </p>
              </div>
            </div>
            <ArrowRight className="size-4 text-muted-foreground group-hover:text-blue-500 transition-colors" />
          </CardContent>
        </Card>

        {/* Metric 2: Live Inquiries / Chat */}
        <Card
          className="border-border/80 bg-card/80 backdrop-blur-xl hover:border-emerald-500/50 transition-all cursor-pointer group"
          onClick={() => navigate(appRoutes.customer.messages)}
        >
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500 group-hover:scale-105 transition-transform">
                <MessageSquare className="size-5.5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Inquiries &amp; Chat</p>
                <p className="font-display text-2xl font-bold text-foreground mt-0.5">Active</p>
              </div>
            </div>
            <ArrowRight className="size-4 text-muted-foreground group-hover:text-emerald-500 transition-colors" />
          </CardContent>
        </Card>

        {/* Metric 3: Verification & Security */}
        <Card className="border-border/80 bg-card/80 backdrop-blur-xl">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                <ShieldCheck className="size-5.5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Profile Verification</p>
                <p className="font-display text-2xl font-bold text-foreground mt-0.5">
                  {isDashboardLoading ? '...' : `${dashboard?.profileCompletion ?? 100}%`}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-[10px] font-bold text-amber-500 border-amber-500/30">
              Verified
            </Badge>
          </CardContent>
        </Card>

        {/* Metric 4: Merchant Privileges Tier */}
        <Card
          className="border-border/80 bg-card/80 backdrop-blur-xl hover:border-purple-500/50 transition-all cursor-pointer group"
          onClick={() =>
            isApprovedMerchant
              ? navigate(role === 'DEALER' ? '/dealer' : '/seller')
              : navigate(appRoutes.customer.becomeSeller)
          }
        >
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3.5">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500 group-hover:scale-105 transition-transform">
                <Sparkles className="size-5.5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium">Platform Membership</p>
                <p className="font-display text-sm font-bold text-foreground mt-1 truncate">
                  {isApprovedMerchant ? `${role} Tier` : 'Individual Buyer'}
                </p>
              </div>
            </div>
            <ArrowRight className="size-4 text-muted-foreground group-hover:text-purple-500 transition-colors" />
          </CardContent>
        </Card>
      </div>

      {/* ── 3. FAST ACTION INTERACTIVE TILES HUB (6 TILES) ─────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-base font-bold text-foreground">Fast Client Actions</h2>
          <span className="text-xs text-muted-foreground">Quick shortcuts &amp; service navigation</span>
        </div>

        <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {/* Tile 1 */}
          <Link
            to={appRoutes.cars}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-[#CF2F2F]/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-[#CF2F2F]/10 text-[#CF2F2F] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Car className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-[#CF2F2F] transition-colors">
              Browse Cars
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">Explore supercar inventory</span>
          </Link>

          {/* Tile 2 */}
          <Link
            to={appRoutes.parts}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-blue-500/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Wrench className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-blue-500 transition-colors">
              OEM Car Parts
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">Exhausts, brakes, wheels</span>
          </Link>

          {/* Tile 3 */}
          <Link
            to={appRoutes.customer.orders}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-emerald-500/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Truck className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-emerald-500 transition-colors">
              Track Orders
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">Dispatches &amp; invoices</span>
          </Link>

          {/* Tile 4 */}
          <Link
            to={appRoutes.customer.messages}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-purple-500/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-purple-500/10 text-purple-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <MessageSquare className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-purple-500 transition-colors">
              Live Inquiries
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">Direct dealer chat</span>
          </Link>

          {/* Tile 5 */}
          <Link
            to={appRoutes.customer.becomeSeller}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-amber-500/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Store className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-amber-500 transition-colors">
              Become Seller
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">List personal vehicles</span>
          </Link>

          {/* Tile 6 */}
          <Link
            to={appRoutes.customer.becomeDealer}
            className="flex flex-col p-4 rounded-xl border border-border/70 bg-card/60 hover:bg-accent/40 hover:border-rose-500/40 transition-all group"
          >
            <div className="size-9 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Building2 className="size-5" />
            </div>
            <span className="font-bold text-xs text-foreground group-hover:text-rose-500 transition-colors">
              Become Dealer
            </span>
            <span className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">Commercial showroom</span>
          </Link>
        </div>
      </div>

      {/* ── 4. TWO-COLUMN ACTIVITY & ONBOARDING SECTION ─────────────────── */}
      <div className="grid gap-6 lg:grid-cols-12 items-start">
        {/* LEFT COLUMN: Recent Orders & Onboarding Roadmap (7 of 12) */}
        <div className="space-y-6 lg:col-span-7">
          {/* Recent Orders Showcase */}
          <Card className="border-border/80 bg-card/80 backdrop-blur-xl overflow-hidden shadow-sm">
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2.5">
                  <PackageOpen className="size-5 text-[#CF2F2F]" />
                  <h3 className="font-display text-base font-bold text-foreground">Recent Spare Parts Orders</h3>
                </div>
                {orders.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(appRoutes.customer.orders)}
                    className="gap-1 text-xs text-[#CF2F2F] font-bold"
                  >
                    View All ({orders.length}) <ArrowRight className="size-3" />
                  </Button>
                )}
              </div>

              {isOrdersLoading ? (
                <div className="py-8 text-center text-xs text-muted-foreground">Loading your orders...</div>
              ) : orders.length === 0 ? (
                <div className="py-8 text-center space-y-3">
                  <div className="size-12 rounded-full bg-accent/60 mx-auto flex items-center justify-center text-muted-foreground">
                    <ShoppingBag className="size-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-bold text-foreground">No Orders Placed Yet</p>
                    <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                      Explore our OEM and aftermarket performance car parts catalog and have items delivered directly.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(appRoutes.parts)}
                    className="bg-[#CF2F2F] hover:bg-[#b52626] text-white text-xs font-bold gap-2"
                  >
                    <ShoppingBag className="size-3.5" /> Browse Parts Catalog
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.slice(0, 3).map((ord: any) => {
                    const totalNum =
                      typeof ord.totalAmount === 'number'
                        ? ord.totalAmount
                        : Number(String(ord.totalAmount).replace(/[^0-9.]/g, '')) || 0
                    const orderDate = new Date(ord.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                    const status = ord.orderStatus || ord.status || 'CONFIRMED'

                    return (
                      <div
                        key={ord.id}
                        onClick={() => navigate(appRoutes.customer.orders)}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-border/70 bg-background/50 hover:bg-muted/40 transition-colors cursor-pointer"
                      >
                        <div className="space-y-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-foreground">#{ord.orderNumber}</span>
                            <Badge
                              className={
                                status === 'DELIVERED'
                                  ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px] font-bold'
                                  : status === 'SHIPPED'
                                  ? 'bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px] font-bold'
                                  : 'bg-purple-500/10 text-purple-500 border-purple-500/20 text-[10px] font-bold'
                              }
                            >
                              {status}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground truncate max-w-md">
                            {ord.items?.map((it: any) => `${it.title} (x${it.quantity})`).join(', ') ||
                              'Automotive components'}
                          </p>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-4 text-xs shrink-0">
                          <span className="text-muted-foreground">{orderDate}</span>
                          <span className="font-display font-extrabold text-foreground text-sm">
                            {formatCurrency(totalNum)}
                          </span>
                          <ChevronRight className="size-4 text-muted-foreground" />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Merchant Onboarding Roadmap Card */}
          <Card className="border-border/80 bg-card/80 backdrop-blur-xl overflow-hidden shadow-sm">
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2.5">
                  <Store className="size-5 text-amber-500" />
                  <h3 className="font-display text-base font-bold text-foreground">Merchant Onboarding Roadmap</h3>
                </div>
                {pendingRequest ? (
                  <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] font-bold">
                    <Clock className="size-3 mr-1" /> Application In Review
                  </Badge>
                ) : isApprovedMerchant ? (
                  <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px] font-bold">
                    <CheckCircle2 className="size-3 mr-1" /> Merchant Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] font-bold">
                    4 Step Verification
                  </Badge>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-4 pt-1">
                {/* Step 1 */}
                <div className="p-3.5 rounded-xl border border-emerald-500/30 bg-emerald-500/5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="size-3.5" /> 1. Registered
                  </div>
                  <p className="text-[11px] text-muted-foreground">Buyer account authenticated</p>
                </div>

                {/* Step 2 */}
                <div
                  className={`p-3.5 rounded-xl border space-y-1.5 ${
                    pendingRequest || isApprovedMerchant
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-border/70 bg-accent/20'
                  }`}
                >
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold ${
                      pendingRequest || isApprovedMerchant
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : 'text-foreground'
                    }`}
                  >
                    {pendingRequest || isApprovedMerchant ? (
                      <CheckCircle2 className="size-3.5" />
                    ) : (
                      <span className="size-3.5 rounded-full bg-muted flex items-center justify-center text-[9px]">2</span>
                    )}
                    2. Apply
                  </div>
                  <p className="text-[11px] text-muted-foreground">KYC / Dealership documents</p>
                </div>

                {/* Step 3 */}
                <div
                  className={`p-3.5 rounded-xl border space-y-1.5 ${
                    isApprovedMerchant
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : pendingRequest
                      ? 'border-amber-500/40 bg-amber-500/10'
                      : 'border-border/70 bg-accent/20'
                  }`}
                >
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold ${
                      isApprovedMerchant
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : pendingRequest
                        ? 'text-amber-500'
                        : 'text-foreground'
                    }`}
                  >
                    {isApprovedMerchant ? (
                      <CheckCircle2 className="size-3.5" />
                    ) : (
                      <span className="size-3.5 rounded-full bg-muted flex items-center justify-center text-[9px]">3</span>
                    )}
                    3. Review
                  </div>
                  <p className="text-[11px] text-muted-foreground">Admin compliance check</p>
                </div>

                {/* Step 4 */}
                <div
                  className={`p-3.5 rounded-xl border space-y-1.5 ${
                    isApprovedMerchant ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-border/70 bg-accent/20'
                  }`}
                >
                  <div
                    className={`flex items-center gap-1.5 text-xs font-bold ${
                      isApprovedMerchant ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
                    }`}
                  >
                    {isApprovedMerchant ? (
                      <CheckCircle2 className="size-3.5" />
                    ) : (
                      <span className="size-3.5 rounded-full bg-muted flex items-center justify-center text-[9px]">4</span>
                    )}
                    4. Start Selling
                  </div>
                  <p className="text-[11px] text-muted-foreground">Inventory &amp; lead management</p>
                </div>
              </div>

              {!isApprovedMerchant && (
                <div className="pt-2 flex flex-wrap items-center gap-3">
                  {pendingRequest ? (
                    <Button
                      variant="outline"
                      onClick={() => navigate(appRoutes.customer.approvalHistory)}
                      className="gap-2 text-xs font-bold border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
                    >
                      <Clock className="size-3.5" /> Check Verification Timeline
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        onClick={() => navigate(appRoutes.customer.becomeSeller)}
                        className="bg-[#CF2F2F] hover:bg-[#b52626] text-white text-xs font-bold gap-2"
                      >
                        <Store className="size-3.5" /> Apply as Independent Seller
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(appRoutes.customer.becomeDealer)}
                        className="text-xs font-bold gap-2"
                      >
                        <Building2 className="size-3.5" /> Apply as Commercial Dealership
                      </Button>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT COLUMN: Account Health, Security, & Privileges (5 of 12) */}
        <div className="space-y-6 lg:col-span-5">
          {/* Security & Buyer Protection */}
          <Card className="border-border/80 bg-card/80 backdrop-blur-xl overflow-hidden shadow-sm">
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="flex items-center gap-2.5 border-b border-border/60 pb-3">
                <ShieldCheck className="size-5 text-emerald-500" />
                <h3 className="font-display text-base font-bold text-foreground">Buyer Protection &amp; Trust</h3>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50 border border-border/60">
                  <CheckCircle2 className="size-4 text-emerald-500 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="text-xs font-bold text-foreground">100% Genuine Car Parts Guarantee</p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      All parts shipped from verified distributors with authorized manufacturer warranties.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50 border border-border/60">
                  <ShieldCheck className="size-4 text-blue-500 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="text-xs font-bold text-foreground">Direct Dealership Inquiries</p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Chat directly with verified luxury sellers and schedule physical car viewings safely.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 rounded-xl bg-background/50 border border-border/60">
                  <Lock className="size-4 text-amber-500 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="text-xs font-bold text-foreground">Encrypted Security &amp; Escrow</p>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      All payment transitions processed via secure PCI-DSS Razorpay gateways.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Rights & Permissions */}
          <Card className="border-border/80 bg-card/80 backdrop-blur-xl overflow-hidden shadow-sm">
            <CardContent className="p-5 sm:p-6 space-y-4">
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2.5">
                  <Zap className="size-5 text-[#CF2F2F]" />
                  <h3 className="font-display text-base font-bold text-foreground">Current Platform Privileges</h3>
                </div>
                <Badge variant="outline" className="text-[10px] font-bold">
                  {role || 'CUSTOMER'}
                </Badge>
              </div>

              <div className="space-y-2">
                {permissionList.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/50 px-3.5 py-2.5"
                  >
                    <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
                    <Badge variant={item.allowed ? 'default' : 'outline'} className="gap-1 text-[10px] font-bold">
                      {item.allowed ? <CheckCircle2 className="size-3" /> : <Lock className="size-3 text-muted-foreground" />}
                      {item.allowed ? 'Enabled' : 'Locked'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Customer Concierge Assistance */}
          <div className="rounded-2xl border border-border/80 bg-gradient-to-br from-card to-accent/30 p-5 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Headphones className="size-5" />
              </div>
              <div>
                <h4 className="font-bold text-xs text-foreground">Need Custom Sourcing?</h4>
                <p className="text-[11px] text-muted-foreground">
                  Our supercar specialists help source rare OEM parts &amp; imported models.
                </p>
              </div>
            </div>

            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full text-xs font-bold gap-2 border-border/80 hover:bg-accent"
            >
              <Link to={appRoutes.customer.messages}>
                <MessageSquare className="size-3.5" /> Contact Customer Concierge
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
